import { useEffect, useRef, useState } from "react";

/**
 * A textarea whose text is the source of truth while it is being typed in.
 *
 * The obvious implementation — render the parsed value back out on every
 * keystroke — cannot be typed into. A line only parses once it is complete, so
 * an incomplete line is dropped, re-rendered away, and the character just typed
 * disappears. "0.75 = 75%" can never be reached because "0" alone parses to
 * nothing.
 *
 * So the raw text is held locally and parsed outward on change. It re-seeds
 * from the parsed value only when the field is not focused, which keeps it in
 * step with an edit made elsewhere without fighting the person typing.
 */
export default function TextListField({
  value,
  toText,
  fromText,
  onChange,
  rows = 3,
  className = "",
  placeholder,
}) {
  const [text, setText] = useState(() => toText(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(toText(value));
    // toText is inline in callers and would re-run this every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <textarea
      rows={rows}
      className={className}
      value={text}
      placeholder={placeholder}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
        // Tidy up to the canonical rendering once editing has finished.
        setText(toText(fromText(text)));
      }}
      onChange={(event) => {
        setText(event.target.value);
        onChange(fromText(event.target.value));
      }}
    />
  );
}
